import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import CloudflareTurnstile, { TURNSTILE_ENABLED } from "../components/security/CloudflareTurnstile";
import { EnvelopeSimple, CircleNotch, ArrowLeft, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

/**
 * ForgotPasswordPage — public route /forgot-password.
 *
 * Uses Commandix dark/orange branding (matches LoginPage), NOT a tenant
 * brand. This is a Commandix-account-recovery flow, not a tenant-business
 * action.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (TURNSTILE_ENABLED && !turnstileToken) {
      setError("Confirma que eres una persona antes de continuar.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
        turnstile_token: turnstileToken || undefined,
      });
      setDone(true);
    } catch (err) {
      // Backend always returns 200 with generic message, so any failure here
      // is a network/server error — show a soft message.
      setError(err?.response?.data?.detail || "No se pudo procesar la solicitud. Intenta de nuevo.");
    } finally {
      setLoading(false);
      if (TURNSTILE_ENABLED) {
        setTurnstileToken("");
        setTurnstileResetKey((value) => value + 1);
      }
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-12" style={{ background: "#0a0a0a" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3" style={{ background: "rgba(234,138,40,0.12)", border: "1px solid rgba(234,138,40,0.25)" }}>
            <ShieldCheck size={28} weight="duotone" color="#ea8a28" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: "#f5f5f5", letterSpacing: "-0.5px" }}>Commandix</h1>
          <p className="text-xs uppercase tracking-[0.2em] mt-1" style={{ color: "#9ca3af" }}>Recuperación de cuenta</p>
        </div>

        <Card className="border" style={{ background: "#141414", borderColor: "rgba(255,255,255,0.08)" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg" style={{ color: "#f5f5f5" }}>
              {done ? "Revisa tu correo" : "Restablecer contraseña"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {done ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg" style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.2)" }}>
                  <CheckCircle size={20} weight="fill" color="#34d399" className="shrink-0 mt-0.5" />
                  <div className="text-sm" style={{ color: "#f5f5f5" }}>
                    <p>Si existe una cuenta asociada a <span className="font-semibold">{email}</span>, recibirás instrucciones para recuperar tu acceso.</p>
                    <p className="mt-2 text-xs" style={{ color: "#9ca3af" }}>
                      El enlace expira en 60 minutos. Revisa también tu carpeta de spam.
                    </p>
                  </div>
                </div>
                <Link to="/login" className="inline-flex items-center gap-2 text-sm hover:underline" style={{ color: "#ea8a28" }} data-testid="forgot-back-login">
                  <ArrowLeft size={14} /> Volver a iniciar sesión
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <p className="text-sm" style={{ color: "#9ca3af", lineHeight: 1.6 }}>
                  Ingresa el correo asociado a tu cuenta de Commandix. Te enviaremos un enlace seguro
                  para crear una nueva contraseña.
                </p>
                <div className="relative">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2" color="#6b7280" />
                  <Input
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border"
                    style={{ background: "#0a0a0a", borderColor: "rgba(255,255,255,0.08)", color: "#f5f5f5" }}
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
                <CloudflareTurnstile key={turnstileResetKey} action="password_reset" onTokenChange={setTurnstileToken} />
                {error && (
                  <p className="text-sm" style={{ color: "#f87171" }}>{error}</p>
                )}
                <Button type="submit" disabled={loading || !email.trim() || (TURNSTILE_ENABLED && !turnstileToken)}
                  className="w-full font-semibold"
                  style={{ background: "#ea8a28", color: "#0a0a0a" }}
                  data-testid="forgot-submit-btn">
                  {loading ? <CircleNotch size={16} className="mr-2 animate-spin" /> : null}
                  {loading ? "Enviando..." : "Enviar enlace de recuperación"}
                </Button>
                <Link to="/login" className="block text-center text-xs hover:underline pt-2" style={{ color: "#9ca3af" }} data-testid="forgot-back-login-link">
                  Volver a iniciar sesión
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[10px] mt-6" style={{ color: "#6b7280" }}>
          Commandix Security · security@commandixcrm.com
        </p>
      </div>
    </div>
  );
}
